import { Action, combineReducers, configureStore, ThunkAction } from '@reduxjs/toolkit';
import { errorNotificationReducer } from 'components/common/ErrorNotification/errorNotificationSlice';
import { connectRouter, routerMiddleware } from 'connected-react-router';
import { authActions, authReducer } from 'features/Auth/authSlice';
import { barInfoListReducer } from 'features/BarGuideInfo/barInfoSlice';
import { barMenuListReducer } from 'features/BarGuideInfoMenu/barMenuSlice';
import { characterSettingReducer } from 'features/CharacterSetting/characterSettingSlice';
import { appReducer } from 'features/common/appSlice';
import { companyReducer } from 'features/common/companySlice';
import { editModeReducer } from 'features/common/editModeSlice';
import { schemaReducer } from 'features/common/schemaSlice';
import { screenReducer } from 'features/common/screenSlice';
import { workerReducer } from 'features/common/workerSlice';
import { deliveryReducer } from 'features/Delivery/deliverySlice';
import { eatingWaySettingReducer } from 'features/EatingWay/eatingWaySettingSlice';
import { menuBookEditorReducer } from 'features/MenuBook/menuBookEditorSlice';
import { menuDetailReducer } from 'features/MenuDetail/menuDetailSlice';
import { menuDetailListReducer } from 'features/MenuList/menuSlice';
import { overlayReducer } from 'features/OverlayFunction/overlaySlice';
import { timeFrameMenuBookReducer } from 'features/SaleType/timeFrameMenuBookSlice';
import { serviceMenuReducer } from 'features/ServiceMenu/serviceMenuSlice';
import { storeSettingDetailReducer } from 'features/Store/storeSettingDetailSlice';
import { subMenuGroupReducer } from 'features/SubMenuList/subMenuSlice';
import { videoFunctionReducer } from 'features/VideoFunction/videoFunctionSlice';
import { PERSIST, persistStore, PURGE, REHYDRATE } from 'redux-persist';
import persistReducer from 'redux-persist/es/persistReducer';
import autoMergeLevel2 from 'redux-persist/es/stateReconciler/autoMergeLevel2';
import storage from 'redux-persist/lib/storage';
import createSagaMiddleware from 'redux-saga';
import { Config, createStateSyncMiddleware, initStateWithPrevTab } from 'redux-state-sync';
import { history } from 'utils';
import rootSaga from './rootSaga';

const rootReducer = combineReducers({
  router: connectRouter(history),
  app: appReducer,
  editMode: editModeReducer,
  company: companyReducer,
  auth: authReducer,
  errorNotification: errorNotificationReducer,
  subMenuGroup: subMenuGroupReducer,
  menuDetailList: menuDetailListReducer,
  menuDetailReducer: menuDetailReducer,
  timeFrameMenuBook: timeFrameMenuBookReducer,
  menuBookEditor: menuBookEditorReducer,
  storeSettingDetail: storeSettingDetailReducer,
  delivery: deliveryReducer,
  barInfo: barInfoListReducer,
  barMenu: barMenuListReducer,
  eatingWaySetting: eatingWaySettingReducer,
  overlay: overlayReducer,
  videoFunction: videoFunctionReducer,
  serviceMenu: serviceMenuReducer,
  characterSetting: characterSettingReducer,
  screen: screenReducer,
  worker: workerReducer,
  schema: schemaReducer,
});

const persistConfig = {
  key: 'root',
  storage: storage,
  whitelist: ['auth', 'editMode', 'screen', 'worker', 'schema', 'delivery'],
  stateReconciler: autoMergeLevel2,
};

const reduxStateSyncConfig: Config = {
  channel: 'as_broadcast_channel',
  whitelist: [authActions.actionLogout.type],
  blacklist: [PERSIST, PURGE, REHYDRATE],
  broadcastChannelOption: { type: 'localstorage' },
  predicate: (action) => {
    const blacklist = [PERSIST, PURGE, REHYDRATE];
    const whitelist = [authActions.actionLogout.type];
    if (typeof action !== 'function') {
      if (Array.isArray(whitelist)) {
        return whitelist.indexOf(action.type) >= 0;
      }

      if (Array.isArray(blacklist)) {
        return blacklist.indexOf(action.type) < 0;
      }
    }
    return false;
  },
};

const persistedReducer = persistReducer<ReturnType<typeof rootReducer>>(persistConfig, rootReducer);

const sagaMiddleware = createSagaMiddleware();

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      thunk: true,
      serializableCheck: false,
      immutableCheck: true,
    }).concat(sagaMiddleware, routerMiddleware(history), createStateSyncMiddleware(reduxStateSyncConfig)),
});

// this is used to pass store.dispatch to the message listener
// init state with other tabs
initStateWithPrevTab(store);

sagaMiddleware.run(rootSaga);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>;
export const persistor = persistStore(store);
